import {
  Button,
  Calendar,
  calendarLocale,
  cn,
  type DateRange,
} from '@up2stay/up2promo-ui';
import { useEffect, useState } from 'react';

import { useLocale } from '@/core/hooks/use-locale';
import type { Dictionary } from '@/core/providers/dictionary-provider';
import { useDictionary } from '@/core/providers/dictionary-provider';

import type {
  ComparisonMode,
  DateRangePreset,
  DateRangeValue,
} from './date-range-picker.types';
import {
  COMPARISON_ORDER,
  endOfDay,
  isComparisonSupported,
  PRESET_ORDER,
  startOfDay,
  valuesEqual,
} from './date-range-picker.types';

export type DateRangePickerLayout = 'compact' | 'desktop-popover';

export interface DateRangePickerContentProps {
  layout: DateRangePickerLayout;
  value: DateRangeValue;
  draftValue: DateRangeValue;
  onDraftValueChange: (value: DateRangeValue) => void;
  showCalendar: boolean;
  onShowCalendarChange: (show: boolean) => void;
  draftComparison: ComparisonMode;
  onDraftComparisonChange: (mode: ComparisonMode) => void;
  onComparisonChange?: (mode: ComparisonMode) => void;
  onPresetCommit: (preset: DateRangePreset) => void;
  onValueCommit: (value: DateRangeValue) => void;
  /** Strip padding/backgrounds when nested inside another panel (e.g. filter popover). */
  embedded?: boolean;
  className?: string;
}

export function DateRangePickerContent({
  layout,
  draftValue,
  onDraftValueChange,
  showCalendar,
  onShowCalendarChange,
  draftComparison,
  onDraftComparisonChange,
  onComparisonChange,
  onPresetCommit,
  onValueCommit,
  embedded = false,
  className,
}: DateRangePickerContentProps) {
  const dict = useDictionary() as Dictionary;
  const d = dict.dateRange;
  const locale = useLocale();

  const selected: DateRange | undefined =
    draftValue.from || draftValue.to
      ? {
          from: draftValue.from ? new Date(draftValue.from) : undefined,
          to: draftValue.to ? new Date(draftValue.to) : undefined,
        }
      : undefined;

  const activePreset = showCalendar ? 'custom' : draftValue.preset;

  const handlePresetClick = (preset: DateRangePreset) => {
    if (preset === 'custom') {
      onShowCalendarChange(true);
      onDraftValueChange({ ...draftValue, preset: 'custom' });
      return;
    }
    onPresetCommit(preset);
  };

  const handleCustomSelect = (range: DateRange | undefined) => {
    const next: DateRangeValue = {
      preset: 'custom',
      from: range?.from ? startOfDay(range.from).toISOString() : undefined,
      to: range?.to ? endOfDay(range.to).toISOString() : undefined,
    };
    onDraftValueChange(next);
    if (next.from && next.to) {
      onValueCommit(next);
    }
  };

  const presetButtons = PRESET_ORDER.map((preset) => {
    const selected =
      preset === 'custom' ? showCalendar : activePreset === preset;
    return (
      <Button
        key={preset}
        type="button"
        title={d.presets[preset]}
        variant={selected ? 'secondary' : 'outline'}
        size="sm"
        className={cn(
          embedded
            ? 'h-7 w-full min-w-0 justify-center rounded-md px-2.5 text-xs font-medium shadow-none'
            : layout === 'compact'
              ? 'h-9 w-full min-w-0 justify-center px-2 text-xs'
              : 'w-full shrink-0 justify-start',
          embedded &&
            selected &&
            'border-primary/40 bg-primary/10 text-primary hover:bg-primary/10',
          embedded &&
            !selected &&
            'border-border/60 bg-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground',
        )}
        onClick={() => handlePresetClick(preset)}
      >
        <span className="truncate">{d.presets[preset]}</span>
      </Button>
    );
  });

  const calendarVisible = layout === 'desktop-popover' ? true : showCalendar;

  return (
    <div className={cn('flex flex-col', className)}>
      <div
        className={cn(
          layout === 'desktop-popover'
            ? 'flex flex-row'
            : embedded
              ? 'flex min-w-0 flex-col gap-2'
              : 'flex flex-col gap-3 p-3',
        )}
      >
        <div
          className={cn(
            layout === 'desktop-popover'
              ? 'flex w-[9.5rem] shrink-0 flex-col gap-1 border-r p-2'
              : embedded
                ? 'grid min-w-0 grid-cols-2 gap-1.5'
                : 'grid min-w-0 grid-cols-2 gap-2',
          )}
        >
          {presetButtons}
        </div>
        {calendarVisible && (
          <div
            className={cn(
              layout === 'desktop-popover'
                ? 'p-1'
                : embedded
                  ? 'border-t border-border/40 pt-2'
                  : 'border-t pt-3',
            )}
          >
            <Calendar
              mode="range"
              locale={calendarLocale(locale)}
              numberOfMonths={layout === 'desktop-popover' ? 2 : 1}
              selected={selected}
              onSelect={handleCustomSelect}
              defaultMonth={selected?.from}
              className={cn(layout === 'compact' && 'mx-auto')}
            />
          </div>
        )}
      </div>

      {onComparisonChange && isComparisonSupported(draftValue) && (
        <div className="flex flex-wrap items-center gap-2 border-t p-3">
          <span className="text-muted-foreground text-xs font-medium">
            {d.comparison.label}
          </span>
          <div className="flex flex-wrap gap-1">
            {COMPARISON_ORDER.map((mode) => (
              <Button
                key={mode}
                type="button"
                variant={draftComparison === mode ? 'secondary' : 'ghost'}
                size="sm"
                className="shrink-0"
                onClick={() => {
                  onDraftComparisonChange(mode);
                  onComparisonChange?.(mode);
                }}
              >
                {d.comparison[mode]}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function useDateRangePickerSession({
  isOpen,
  value,
  comparison,
  layout,
}: {
  isOpen: boolean;
  value: DateRangeValue;
  comparison?: ComparisonMode;
  layout: DateRangePickerLayout;
}) {
  const resolvedComparison = comparison ?? 'none';

  const [draftValue, setDraftValue] = useState(value);
  const [draftComparison, setDraftComparison] =
    useState<ComparisonMode>(resolvedComparison);
  const [showCalendar, setShowCalendar] = useState(
    layout === 'desktop-popover' ? true : value.preset === 'custom',
  );

  useEffect(() => {
    if (!isOpen) return;

    queueMicrotask(() => {
      setDraftValue(value);
      setDraftComparison(resolvedComparison);
      setShowCalendar(
        layout === 'desktop-popover' ? true : value.preset === 'custom',
      );
    });
  }, [isOpen, layout, resolvedComparison, value]);

  const hasDraftChanges =
    !valuesEqual(draftValue, value) ||
    (comparison != null && draftComparison !== resolvedComparison);

  return {
    draftValue,
    setDraftValue,
    draftComparison,
    setDraftComparison,
    showCalendar,
    setShowCalendar,
    hasDraftChanges,
  };
}
